import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { completeCornerstoneLogin } from '../../lib/cornerstone';

/**
 * OAuth redirect target — `VITE_CORNERSTONE_REDIRECT_URI` points here.
 *
 * Cornerstone sends the browser back with `?code=…&state=…`; this page redeems
 * the code for tokens and then hands off to the app. It renders a plain status
 * while that happens, because the user is mid-redirect and a blank screen here
 * reads as a broken login.
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // React 18+ StrictMode double-invokes effects in development. The code is
  // single-use, so a second exchange would fail against a now-spent code and
  // show a spurious error. Guard the run, not the effect.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    completeCornerstoneLogin(window.location.search)
      .then(() => {
        // replace: true so Back does not return to a spent authorization code.
        navigate('/dashboard', { replace: true });
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Sign-in failed.');
      });
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-surface)] px-4">
      <div className="w-full max-w-sm text-center">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-[var(--color-text)]">Sign-in failed</h1>
            <p className="mt-2 text-sm text-[var(--color-text-muted)]">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/sign-in', { replace: true })}
              className="mt-6 w-full rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <h1 className="text-lg font-semibold text-[var(--color-text)]">Signing you in…</h1>
            <p className="mt-2 text-sm text-[var(--color-text-muted)]">
              Completing sign-in with Cornerstone.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
